import { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Upload, RotateCcw, Pencil, GripVertical, Plus, Trash2, Crop, Search, Image as ImageIcon, Type, Layout, Radar, PanelBottom } from "lucide-react";

export const inputCls = "w-full bg-[#141414] border border-white/10 text-white text-sm p-2.5 focus:border-[#ff0033] outline-none placeholder:text-neutral-600";
export const labelCls = "block font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase mb-1";

const Panel = ({ icon: Icon, title, hint, children, testId, open = false }) => {
  const [o, setO] = useState(open);
  return (
    <details className="border border-white/10 bg-[#0b0b0b]" data-testid={testId} open={o}
      onToggle={(e) => setO(e.currentTarget.open)}>
      <summary className="cursor-pointer px-4 py-3 flex items-center justify-between select-none">
        <span className="font-mono-lab text-[11px] uppercase tracking-widest text-[#ff0033] flex items-center gap-2">
          <Icon size={13} /> {title}
        </span>
        <span className="font-mono-lab text-[10px] text-neutral-500 uppercase">{hint || "Bearbeiten"}</span>
      </summary>
      <div className="p-4 border-t border-white/10 space-y-3">{children}</div>
    </details>
  );
};

const Field = ({ label, value, onChange, testId, rows, placeholder }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {rows ? (
      <textarea value={value || ""} rows={rows} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    ) : (
      <input value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
    )}
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-neutral-300 text-sm cursor-pointer">
    <input type="checkbox" checked={checked !== false} onChange={(e) => onChange(e.target.checked)} className="accent-[#ff0033]" data-testid={testId} />
    {label}
  </label>
);

const CheckList = ({ items = [], onChange, testId }) => (
  <div>
    <label className={labelCls}>Checkliste (Häkchen-Punkte)</label>
    <div className="space-y-2">
      {items.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[#ff0033] font-mono-lab text-xs">✓</span>
          <input value={c} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} className={inputCls} data-testid={`${testId}-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-neutral-500 hover:text-[#ff0033]"><X size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ""])} data-testid={`${testId}-add`} className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#ff0033]">+ Punkt hinzufügen</button>
    </div>
  </div>
);

export const HeroEditor = ({ hero = {}, onChange }) => {
  const p = (k, v) => onChange({ ...hero, [k]: v });
  return (
    <Panel icon={Layout} title="Kopfbereich · Überschrift & Begrüßung" testId="nl-edit-hero">
      <Field label="Überschrift (weiß, Zeilenumbruch = neue Zeile)" value={hero.headline} rows={2} onChange={(v) => p("headline", v)} testId="nl-hero-headline" />
      <Field label="Überschrift-Akzent (rot)" value={hero.headline_accent} onChange={(v) => p("headline_accent", v)} testId="nl-hero-accent" />
      <Field label="Begrüßungstext ({{NAME}} wird durch den Vornamen ersetzt)" value={hero.intro} rows={3} onChange={(v) => p("intro", v)} testId="nl-hero-intro" />
      <div className="flex flex-wrap gap-5 pt-1">
        <Toggle label="Logo anzeigen" checked={hero.show_logo} onChange={(v) => p("show_logo", v)} testId="nl-hero-show-logo" />
        <Toggle label="Kopfbild anzeigen" checked={hero.show_image} onChange={(v) => p("show_image", v)} testId="nl-hero-show-image" />
      </div>
      <Field label="Eigene Bild-URL für das Kopfbild (optional – sonst hochgeladenes Bild)" value={hero.image_url} onChange={(v) => p("image_url", v)} testId="nl-hero-image-url" placeholder="https://…" />
    </Panel>
  );
};

export const TrackManEditor = ({ tm = {}, onChange }) => {
  const p = (k, v) => onChange({ ...tm, [k]: v });
  return (
    <Panel icon={Radar} title="TrackMan-Block" testId="nl-edit-trackman">
      <Toggle label="TrackMan-Block im Newsletter anzeigen" checked={tm.enabled} onChange={(v) => p("enabled", v)} testId="nl-tm-enabled" />
      <Field label="Kicker-Zeile (klein, oben)" value={tm.kicker} onChange={(v) => p("kicker", v)} testId="nl-tm-kicker" />
      <Field label="Überschrift" value={tm.title} onChange={(v) => p("title", v)} testId="nl-tm-title" />
      <Field label="Text 1" value={tm.text} rows={2} onChange={(v) => p("text", v)} testId="nl-tm-text" />
      <Field label="Hervorgehobene Zeile (rot)" value={tm.highlight} onChange={(v) => p("highlight", v)} testId="nl-tm-highlight" />
      <Field label="Text 2" value={tm.text2} rows={3} onChange={(v) => p("text2", v)} testId="nl-tm-text2" />
      <CheckList items={tm.checks || []} onChange={(v) => p("checks", v)} testId="nl-tm-check" />
      <div className="border-t border-white/10 pt-3 space-y-3">
        <Toggle label={"„Coming Soon“-Kasten anzeigen"} checked={tm.soon_enabled} onChange={(v) => p("soon_enabled", v)} testId="nl-tm-soon-enabled" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Coming-Soon Kicker" value={tm.soon_kicker} onChange={(v) => p("soon_kicker", v)} testId="nl-tm-soon-kicker" />
          <Field label="Coming-Soon Titel" value={tm.soon_title} onChange={(v) => p("soon_title", v)} testId="nl-tm-soon-title" />
        </div>
        <Field label="Coming-Soon Text" value={tm.soon_text} rows={2} onChange={(v) => p("soon_text", v)} testId="nl-tm-soon-text" />
      </div>
      <Field label="Button-Text" value={tm.cta_label} onChange={(v) => p("cta_label", v)} testId="nl-tm-cta-label" />
      <Field label="Eigene Bild-URL (optional)" value={tm.image_url} onChange={(v) => p("image_url", v)} testId="nl-tm-image-url" placeholder="https://…" />
    </Panel>
  );
};

export const FooterEditor = ({ footer = {}, onChange }) => {
  const p = (k, v) => onChange({ ...footer, [k]: v });
  const links = footer.links || [];
  const setLink = (i, k, v) => p("links", links.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  return (
    <Panel icon={PanelBottom} title="Abschluss & Fußzeile" testId="nl-edit-footer">
      <Field label="Schlusssatz" value={footer.outro} rows={2} onChange={(v) => p("outro", v)} testId="nl-footer-outro" />
      <Field label="Signatur (Zeilenumbruch möglich)" value={footer.signature} rows={2} onChange={(v) => p("signature", v)} testId="nl-footer-signature" />
      <div>
        <label className={labelCls}>Fußzeilen-Links</label>
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input value={l.label || ""} placeholder="Beschriftung" onChange={(e) => setLink(i, "label", e.target.value)} className={`${inputCls} flex-1 min-w-[140px]`} data-testid={`nl-footer-link-label-${i}`} />
              <input value={l.url || ""} placeholder="/kurse oder https://…" onChange={(e) => setLink(i, "url", e.target.value)} className={`${inputCls} flex-1 min-w-[160px]`} data-testid={`nl-footer-link-url-${i}`} />
              <button type="button" onClick={() => p("links", links.filter((_, idx) => idx !== i))} className="text-neutral-500 hover:text-[#ff0033]"><X size={14} /></button>
            </div>
          ))}
          <button type="button" onClick={() => p("links", [...links, { label: "", url: "" }])} data-testid="nl-footer-link-add" className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#ff0033]">+ Link hinzufügen</button>
        </div>
      </div>
      <Field label="Hinweistext (Einwilligung)" value={footer.disclaimer} rows={2} onChange={(v) => p("disclaimer", v)} testId="nl-footer-disclaimer" />
      <Field label="Abmelde-Link-Text" value={footer.unsub_label} onChange={(v) => p("unsub_label", v)} testId="nl-footer-unsub" />
    </Panel>
  );
};

export const SectionEditor = ({ sec, onChange, onRemove, dragHandleProps = {} }) => {
  const patch = (k, v) => onChange({ ...sec, [k]: v });
  const [open, setOpen] = useState(true);
  return (
    <details className="border border-white/10 bg-[#0b0b0b]" data-testid={`nl-edit-section-${sec.key}`} open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary className="cursor-pointer px-4 py-3 flex items-center justify-between select-none">
        <span className="flex items-center gap-2 min-w-0">
          <span {...dragHandleProps} className="text-neutral-500 hover:text-white cursor-grab active:cursor-grabbing"
            title="Zum Umsortieren ziehen" data-testid={`nl-drag-${sec.key}`} onClick={(e) => e.preventDefault()}>
            <GripVertical size={16} />
          </span>
          <span className="font-mono-lab text-[11px] uppercase tracking-widest text-[#ff0033] truncate">
            <Pencil size={12} className="inline mr-2" />{sec.title || sec.key}
          </span>
          {sec.manual && <span className="font-mono-lab text-[9px] uppercase tracking-widest text-neutral-400 border border-white/20 px-1.5 py-0.5">Eigene Sektion</span>}
        </span>
        <span className="flex items-center gap-3">
          <span className="font-mono-lab text-[10px] text-neutral-500 uppercase">Bearbeiten</span>
          {onRemove && (
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (window.confirm(`Sektion „${sec.title || sec.key}“ aus dem Newsletter entfernen?`)) onRemove(); }}
              className="text-neutral-500 hover:text-[#ff0033]" title="Sektion entfernen" data-testid={`nl-remove-section-${sec.key}`}>
              <Trash2 size={14} />
            </button>
          )}
        </span>
      </summary>
      <div className="p-4 border-t border-white/10 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Titel" value={sec.title} onChange={(v) => patch("title", v)} testId={`nl-edit-${sec.key}-title`} />
          <Field label="Termin / Meta" value={sec.when} onChange={(v) => patch("when", v)} testId={`nl-edit-${sec.key}-when`} />
          <Field label="Coach-Zeile" value={sec.coach} onChange={(v) => patch("coach", v)} testId={`nl-edit-${sec.key}-coach`} />
          <Field label="Preis-Zeile" value={sec.price_line} onChange={(v) => patch("price_line", v)} testId={`nl-edit-${sec.key}-price`} />
          <Field label="Plätze-Label" value={sec.spots_label} onChange={(v) => patch("spots_label", v)} testId={`nl-edit-${sec.key}-spots`} />
          <Field label="Thema (optional)" value={sec.theme} onChange={(v) => patch("theme", v)} testId={`nl-edit-${sec.key}-theme`} />
        </div>
        <Field label="Beschreibung" value={sec.text} rows={3} onChange={(v) => patch("text", v)} testId={`nl-edit-${sec.key}-text`} />
        <CheckList items={sec.checks || []} onChange={(v) => patch("checks", v)} testId={`nl-edit-${sec.key}-check`} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Button-Text" value={sec.cta_label} onChange={(v) => patch("cta_label", v)} testId={`nl-edit-${sec.key}-cta-label`} placeholder="Zur Buchung" />
          {sec.manual && <Field label="Button-Ziel (URL oder /pfad)" value={sec.cta_url_raw} onChange={(v) => patch("cta_url_raw", v)} testId={`nl-edit-${sec.key}-cta-url`} placeholder="/kurse" />}
        </div>
        <Field label="Eigene Bild-URL (optional – sonst hochgeladenes Bild)" value={sec.image_url} onChange={(v) => patch("image_url", v)} testId={`nl-edit-${sec.key}-image-url`} placeholder="https://…" />
      </div>
    </details>
  );
};

// Extrahiert einen 600×300-Ausschnitt (2:1) aus dem Original nach Cropper-Daten.
async function cropToBlob(imgSrc, cropPx, mime = "image/jpeg") {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = imgSrc;
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
  const outW = 1200, outH = 600;
  const canvas = document.createElement("canvas");
  canvas.width = outW; canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(img, cropPx.x, cropPx.y, cropPx.width, cropPx.height, 0, 0, outW, outH);
  return await new Promise((res) => canvas.toBlob((b) => res(b), mime, 0.9));
}

export const CropModal = ({ src, aspect = 2, filename = "bild.jpg", onConfirm, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const onComplete = useCallback((_, px) => setPixels(px), []);

  const confirm = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(src, pixels, "image/jpeg");
      onConfirm(new File([blob], filename.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
    } catch (e) { toast.error("Zuschnitt fehlgeschlagen"); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="nl-crop-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onCancel} />
      <div className="relative bg-[#0A0A0A] border border-[#ff0033]/40 w-full max-w-3xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <p className="font-mono-lab text-[11px] tracking-widest text-[#ff0033] uppercase flex items-center gap-2"><Crop size={13} /> Bildzuschnitt · Format 2:1 (600×300)</p>
          <button onClick={onCancel} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="relative w-full h-[420px] bg-black">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} objectFit="contain" />
        </div>
        <div className="flex items-center gap-3 p-4 border-t border-white/10">
          <label className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase">Zoom</label>
          <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#ff0033]" data-testid="nl-crop-zoom" />
          <button onClick={onCancel} className="border border-white/15 text-neutral-300 px-4 py-2 font-mono-lab text-xs uppercase">Abbrechen</button>
          <button onClick={confirm} disabled={busy || !pixels} data-testid="nl-crop-confirm" className="flex items-center gap-2 bg-[#ff0033] text-white px-4 py-2 font-mono-lab text-xs uppercase hover:bg-[#cc0029] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Crop size={14} />} Zuschneiden & hochladen
          </button>
        </div>
      </div>
    </div>
  );
};

const ImageCard = ({ slot, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [pendingName, setPendingName] = useState("");

  const doUpload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/newsletter/images/${slot.key}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Bild „${slot.label}“ hochgeladen`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); setCropSrc(null); }
  };

  const openCropper = (file) => {
    if (!file.type?.startsWith("image/")) { toast.error("Bitte ein Bild wählen"); return; }
    const reader = new FileReader();
    reader.onload = (e) => { setCropSrc(String(e.target.result)); setPendingName(file.name || `${slot.key}.jpg`); };
    reader.readAsDataURL(file);
  };

  const reset = async () => {
    if (!slot.custom || !window.confirm(`„${slot.label}“ auf Standardbild zurücksetzen?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/newsletter/images/${slot.key}`);
      toast.success("Zurückgesetzt");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const needsCrop = slot.key !== "logo";

  return (
    <div className="border border-white/10 bg-[#0d0d0d] group" data-testid={`nl-img-${slot.key}`}>
      <div className="relative bg-black overflow-hidden" style={{ aspectRatio: "2 / 1" }}>
        {slot.url ? (
          <img src={slot.url} alt={slot.label} className={`w-full h-full transition-transform duration-500 group-hover:scale-[1.03] ${slot.key === "logo" ? "object-contain p-5" : "object-cover"}`} onError={(e) => { e.currentTarget.style.opacity = 0.2; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-600 font-mono-lab text-[10px] uppercase tracking-widest">Noch kein Bild</div>
        )}
        <span className={`absolute top-2 left-2 font-mono-lab text-[9px] uppercase tracking-widest px-2 py-0.5 border ${slot.custom ? "text-[#ff0033] border-[#ff0033]/50 bg-black/70" : "text-neutral-300 border-white/25 bg-black/60"}`}>
          {slot.custom ? "Eigenes Bild" : "Standard"}
        </span>
      </div>
      <div className="p-3">
        <p className="text-white text-sm truncate">{slot.label}</p>
        <p className="text-neutral-500 text-[10px] font-mono-lab uppercase tracking-widest mt-0.5">
          {slot.group} {slot.custom ? `· ${Math.round((slot.size || 0) / 1024)} KB` : ""}
        </p>
        <div className="flex items-center gap-2 mt-3">
          <input ref={inputRef} type="file" accept="image/*" hidden data-testid={`nl-img-upload-${slot.key}`} onChange={(e) => {
            const f = e.target.files?.[0]; if (!f) return;
            if (needsCrop) openCropper(f); else doUpload(f);
            e.target.value = "";
          }} />
          <button disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`nl-img-btn-${slot.key}`} className="flex-1 flex items-center justify-center gap-1 border border-[#ff0033]/50 text-[#ff0033] font-mono-lab text-[10px] uppercase px-3 py-2 hover:bg-[#ff0033]/10 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {slot.custom ? "Tauschen" : "Hochladen"}
          </button>
          {slot.custom && (
            <button disabled={busy} onClick={reset} data-testid={`nl-img-reset-${slot.key}`} className="border border-white/15 text-neutral-300 hover:text-white font-mono-lab text-[10px] uppercase px-2 py-2 disabled:opacity-50" title="Auf Standardbild zurücksetzen">
              <RotateCcw size={12} />
            </button>
          )}
        </div>
      </div>
      {cropSrc && <CropModal src={cropSrc} aspect={2} filename={pendingName} onCancel={() => setCropSrc(null)} onConfirm={doUpload} />}
    </div>
  );
};

export const ImageManager = ({ slots = [], onChanged }) => {
  const groups = slots.reduce((acc, s) => {
    const g = s.group || "Weitere";
    acc[g] = [...(acc[g] || []), s];
    return acc;
  }, {});
  const order = ["Grundgerüst", "Kurs-Sektion", "TrackMan", "Eigene Sektion", "Weitere"];
  const names = Object.keys(groups).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return (
    <div className="border border-white/10 bg-[#0b0b0b] p-4 mb-6" data-testid="nl-images-panel">
      <p className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase mb-1 flex items-center gap-2"><ImageIcon size={13} className="text-[#ff0033]" /> Bilder-Management</p>
      <p className="text-neutral-500 text-xs mb-4">Empfohlen: 1200×600 px (2:1), JPG/PNG/WebP, max. 5 MB. Beim Hochladen kannst Du den Ausschnitt selbst wählen. Getauschte Bilder erscheinen sofort in der Vorschau.</p>
      {names.map((g) => (
        <div key={g} className="mb-5 last:mb-0">
          <p className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{g}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {groups[g].map((s) => <ImageCard key={s.key} slot={s} onChanged={onChanged} />)}
          </div>
        </div>
      ))}
    </div>
  );
};

export const CoursePickerModal = ({ available, existingKeys, onPick, onClose }) => {
  const [q, setQ] = useState("");
  const filtered = (available || []).filter((c) => {
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return c.name.toLowerCase().includes(s) || (c.when || "").toLowerCase().includes(s);
  });
  const makeKey = (name) => {
    const base = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 24) || `k${Date.now()}`;
    let k = base, n = 2;
    while (existingKeys.includes(k)) { k = `${base}-${n++}`; }
    return k;
  };
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="nl-picker-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border border-[#ff0033]/40 w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <p className="font-mono-lab text-[11px] tracking-widest text-[#ff0033] uppercase">Kurs hinzufügen · Automatische Erkennung</p>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 border-b border-white/10">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kurs suchen…" className={`${inputCls} pl-9`} data-testid="nl-picker-search" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-white/5">
          {filtered.length === 0 && <p className="p-6 text-neutral-500 text-sm">Alle buchbaren Kurse sind bereits eingefügt oder kein passender Kurs gefunden.</p>}
          {filtered.map((c) => (
            <button key={c.course_id + c.session_id}
              onClick={() => onPick(c.special ? {
                key: c.image_key || makeKey(c.name), manual: true,
                title: c.name, when: c.when, coach: "",
                price_line: c.price_line || "", spots_label: "", theme: "",
                text: c.description || "", cta_label: c.cta_label || "Mehr erfahren",
                cta_url_raw: c.cta_url_raw || "/kurse", image_url: "",
                checks: c.checks || [],
              } : {
                key: makeKey(c.name), course_id: c.course_id, session_id: c.session_id,
                title: c.name, when: c.when,
                coach: c.coach ? `Mit Coach ${c.coach}` : "",
                price_line: c.price ? `Nur ${Math.round(c.price)} € pro Person` : "",
                spots_label: c.free_spots <= 4 ? `Nur noch ${c.free_spots} Plätze verfügbar` : "Noch Plätze frei!",
                theme: "", text: c.description || "", cta_label: "Zur Buchung", image_url: "",
                checks: ["Direkt online buchen", "Professionelle Betreuung", "Fester Termin"],
              })}
              className="w-full text-left p-4 hover:bg-white/5 flex items-start gap-3" data-testid={`nl-picker-add-${c.course_id}`}>
              <Plus size={16} className="text-[#ff0033] mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{c.name}{c.special && <span className="ml-2 font-mono-lab text-[9px] uppercase tracking-widest text-[#ff0033] border border-[#ff0033]/40 px-1.5 py-0.5">Topic</span>}</p>
                <p className="text-neutral-400 text-xs mt-0.5">{c.when} · {c.coach || (c.special ? c.price_line : "—")}{!c.special && ` · ${c.free_spots} Plätze frei`}</p>
                {!c.special && c.price > 0 && <p className="text-[#ff0033] text-xs font-mono-lab uppercase tracking-widest mt-1">{Math.round(c.price)} € pro Person</p>}
                {c.special && <p className="text-[#ff0033] text-xs font-mono-lab uppercase tracking-widest mt-1">{c.price_line}</p>}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const SubjectField = ({ value, onChange }) => (
  <div>
    <label className={labelCls}><Type size={11} className="inline mr-1" /> Betreff</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid="nl-edit-subject" />
  </div>
);
